import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    inject,
    Input,
    input,
    linkedSignal,
    OnDestroy,
    OnInit,
    output,
    signal,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChatConversation,
    IChatConversationMenuOption,
    IChatConversationMessage,
    IConversationMessageReadBy,
} from '@fi-shared/fi-chat/fi-chat';
import { fromEvent, Subject } from 'rxjs';
import { isDefined } from '@fi-shared/utils';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { takeUntil } from 'rxjs/operators';
import { FiImageSize } from '@fi-shared/types/image-size';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import { AppTextService } from '@fi-shared/app-text';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { FiChatSourceMessageType } from '@fi-shared/fi-chat/fi-chat-source-message-type';
import { VimeoUnavailablePlaceholderSize } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-enum';
import { FiChatConversationHelperService } from '@fi-shared/fi-chat/fi-chat-conversation/fi-chat-conversation-helper.service';
import { IMediaBankFileReference } from '@fi-app/shared/media-bank';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { FiSharedActionsService } from '@fi-app/utilities/fi-services/fi-shared-actions.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiLikesService } from '@fi-app/shared/fi-likes/services/fi-likes.service';
import { FiLikesFactoryService } from '@fi-app/shared/fi-likes/services/fi-likes-factory.service';
import { FiLikesStoreService } from '@fi-app/shared/fi-likes/services/fi-likes-store.service';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { IReactionData, IReactionFavoriteModel, IReactionModel, IEmojiViewData } from '@fi-shared/fi-likes/fi-likes.d';
import { getClientGuid } from '@fi-app/shared/utils/client-guid';
import { IReactionsUpdatedEventArgs } from '@fi-app/shared/fi-signal-r/fi-signal-r';

@Component({
    selector: 'fi-chat-conversation-message',
    templateUrl: './fi-chat-conversation-message.component.html',
    styleUrls: ['./fi-chat-conversation-message.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-message' },
    standalone: false,
})
export class FiChatConversationMessageComponent implements OnInit, OnDestroy {
    public conversationMessage = input<IChatConversationMessage>();
    @Input() conversation: IChatConversation;
    @Input() chatConversationContainerEl: HTMLElement;
    public isMessageDateShown = input<boolean>();
    public isUserProfileImageShown = input<boolean>();
    public isCreatedByUserNameShown = input<boolean>();
    public isLastMessage = input<boolean>();
    public isEmojiMartReactionPanelOpen = input<boolean>(false);

    public toggleReactionEmojiMartVisibility = output<void>();
    public navigateToMessage = output<IChatConversationMessage>();

    @ViewChild('chatConversationMessageContainer', { static: true })
    chatConversationMessageContainerRef: ElementRef;

    protected readonly fiImageSize = FiImageSize;
    protected readonly isDefined = isDefined;
    protected readonly ImageSizes = FiImageSize;
    protected readonly VimeoUnavailablePlaceholderSize = VimeoUnavailablePlaceholderSize;
    protected readonly SourceMessageType = FiChatSourceMessageType;

    protected triggerMessageMenuOpen$ = new Subject<void>();
    protected isMouseOverContainer = signal<boolean>(false);

    private readonly ngOnDestroy$ = new Subject<void>();

    private fiChatService = inject(FiChatService);
    private fiChatSyncService = inject(FiChatSyncService);
    private fiLikesFactoryService = inject(FiLikesFactoryService);
    private fiLikesStoreService = inject(FiLikesStoreService);
    private fiLikeService = inject(FiLikesService);
    private fiSignalRService = inject(FiSignalRService);
    private cdr = inject(ChangeDetectorRef);
    private appTextService = inject(AppTextService);
    private detectDevicesService = inject(DetectDevicesService);
    private fiChatConversationHelperService = inject(FiChatConversationHelperService);
    private mediaStorageService = inject(FiMediaStorageService);
    private mediaViewerService = inject(MediaViewerService);
    private sharedActionsService = inject(FiSharedActionsService);
    private fiUserDataService = inject(FiUserDataService);

    protected profileOptions: ReadonlyArray<IChatConversationMenuOption> = [
        {
            label: this.appTextService.get('chat.conversation_profile_options_view_profile'),
            action: () => this.goToUserProfile(),
            isAvailable: true,
        },
        {
            label: this.appTextService.get('chat.conversation_profile_options_send_message'),
            action: () => this.startOneToOneConversation(),
            isAvailable: this.canStartOneToOneConversation(),
        },
    ];

    protected sourceMessage = computed(() => this.conversationMessage().SourceMessage);
    protected conversationMessageReactions = linkedSignal<IReactionModel[]>(() =>
        this.conversationMessage().Reactions.map((reaction) => {
            return {
                ...reaction,
            };
        }),
    );
    protected conversationMessageReadBy = linkedSignal<IConversationMessageReadBy[]>(
        () => this.conversationMessage().ReadBy,
    );
    protected isOutgoingMessage = computed(() =>
        this.fiChatConversationHelperService.isOutgoingMessage(this.conversationMessage().CreatedByUserId),
    );
    protected isNonDeletedSystemMessage = computed(() =>
        this.fiChatConversationHelperService.isNonDeletedSystemMessage(this.conversationMessage().Type),
    );
    protected isSystemMessage = computed(() =>
        this.fiChatConversationHelperService.isSystemMessage(this.conversationMessage().Type),
    );
    protected isMessageReadStatusShown = computed(() =>
        this.fiChatConversationHelperService.isMessageReadStatusShown(this.conversationMessage(), this.isLastMessage()),
    );

    protected isMessageEdited = computed(() =>
        this.fiChatConversationHelperService.isMessageEdited(this.conversationMessage()),
    );
    protected isConversationMessageInfoShown = computed(() =>
        this.fiChatConversationHelperService.isConversationMessageInfoShown(
            this.conversationMessage(),
            this.isCreatedByUserNameShown(),
        ),
    );
    protected hasAttachments = computed(() =>
        this.fiChatConversationHelperService.hasAttachments(this.conversationMessage()),
    );
    protected isNoPadding = computed(() =>
        this.fiChatConversationHelperService.isNoPadding(this.conversationMessage(), this.isCreatedByUserNameShown()),
    );
    protected isIncomingMessage = computed(() =>
        this.fiChatConversationHelperService.isIncomingMessage(this.conversationMessage()),
    );
    protected isSourceMessageForwarded = computed(() =>
        this.fiChatConversationHelperService.isSourceMessageForwarded(this.conversationMessage()),
    );
    protected isOnlySourceMessage = computed(() =>
        this.fiChatConversationHelperService.isOnlySourceMessage(this.conversationMessage()),
    );
    protected isSourceDeleted = computed(() =>
        this.fiChatConversationHelperService.isSourceDeleted(this.sourceMessage()),
    );
    protected sourceMessageHeader = computed(() =>
        this.fiChatConversationHelperService.getSourceMessageHeader(this.conversationMessage(), this.sourceMessage()),
    );
    protected sourceMessageContent = computed(() =>
        this.fiChatConversationHelperService.getSourceMessageContent(this.conversationMessage(), this.sourceMessage()),
    );
    protected isMessageSenderNameShown = computed(() =>
        this.fiChatConversationHelperService.isMessageSenderNameShown(
            this.conversationMessage(),
            this.isCreatedByUserNameShown(),
        ),
    );
    protected createdByUserName = computed(() =>
        this.fiChatConversationHelperService.getCreatedByUserName(this.conversationMessage()),
    );
    private canNavigeteToSourceMessage = computed(
        () =>
            this.fiChatConversationHelperService.isSourceMessageReplied(this.conversationMessage()) &&
            !this.isSourceDeleted(),
    );

    public ngOnInit(): void {
        this.initializeSubscriptions();
    }

    protected onReactionSelect(reactionData: IReactionData): void {
        const existingReaction = this.conversationMessageReactions().find(
            (r) =>
                r.UserId === this.fiUserDataService.getUserId() &&
                this.fiLikesFactoryService.isReactionDataEquals(r.Data, reactionData),
        );
        if (existingReaction) {
            // user already reacted with this emoji, just ignore
            return;
        }

        const emojiLike = this.fiLikesFactoryService.getNewEmojiLikeInstance(
            this.conversationMessage().Id,
            ObjectTypes.ChatConversationMessage,
            reactionData,
        );
        this.fiLikeService.saveLike(this.fiLikesFactoryService.getLikeInstanceForSave(emojiLike)).then(() => {
            this.fiLikesFactoryService.updateReactions(this.conversationMessageReactions, ActionType.Create, emojiLike);
            this.fiLikesStoreService.updateReactionFavorites(reactionData);
        });
    }

    protected onReactionRemove(reaction: IReactionModel): void {
        const emojiLike = this.fiLikesFactoryService.getNewEmojiLikeInstance(
            this.conversationMessage().Id,
            ObjectTypes.ChatConversationMessage,
        );
        this.fiLikeService.deleteLike(this.fiLikesFactoryService.getLikeInstanceForSave(emojiLike)).then(() => {
            this.fiLikesFactoryService.updateReactions(this.conversationMessageReactions, ActionType.Delete, emojiLike);
        });
    }
    protected onReactionReplace({ reaction, newEmoji }: { reaction: IReactionModel; newEmoji: IEmojiViewData }): void {
        // todo probably we will not use this
        // todo: replace reaction
        // todo: update user favorites reactions
    }

    protected isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    protected onLongPress(): void {
        this.triggerMessageMenuOpen$.next();
    }

    protected navigateToSourceMessage(): void {
        if (this.canNavigeteToSourceMessage()) {
            this.navigateToMessage.emit(this.sourceMessage());
        }
    }

    protected showMediaPreview($event: MouseEvent, mediaBankFileReferences: IMediaBankFileReference[]): void {
        $event.stopPropagation();
        const popupImageUrl = this.mediaStorageService.getMediaFileUrl(mediaBankFileReferences[0], {
            size: FiImageSize.Large,
        });
        this.mediaViewerService.Show(popupImageUrl, undefined, mediaBankFileReferences);
    }

    protected isOnlyAttachmentsMessage(message: IChatConversationMessage): boolean {
        return this.fiChatConversationHelperService.isOnlyAttachmentsMessage(message);
    }

    private initializeSubscriptions(): void {
        this.fiChatSyncService
            .conversationUpdated$(this.conversation.Id)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.cdr.markForCheck());

        this.fiSignalRService.chatConversationMessageReadEvent.subscribe((data) => {
            if (data.ConversationMessageIds.indexOf(this.conversationMessage().Id) !== -1) {
                this.conversationMessageReadBy.update((readBy) => [
                    ...readBy,
                    {
                        ReadByUserId: data.ReadByUserId,
                        ReadOn: data.ReadOn,
                    },
                ]);
            }
        }, this.ngOnDestroy$);

        this.subscribeToReactionsUpdatedEvent();

        fromEvent(this.chatConversationMessageContainerRef.nativeElement, 'mouseenter')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.isMouseOverContainer.set(true));

        fromEvent(this.chatConversationMessageContainerRef.nativeElement, 'mouseleave')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.isMouseOverContainer.set(false));
    }

    private subscribeToReactionsUpdatedEvent(): void {
        this.fiSignalRService.reactionsUpdatedEvent.subscribe((data) => {
            if (!this.isCurrentMessageReactionsUpdated(data)) {
                return;
            }

            this.fiLikesFactoryService.updateReactions(
                this.conversationMessageReactions,
                data.ActionType,
                data.Reactions[0],
            );
        }, this.ngOnDestroy$);
    }

    private isCurrentMessageReactionsUpdated(data: IReactionsUpdatedEventArgs): boolean {
        return (
            data.ObjectId === this.conversationMessage().Id &&
            data.ObjectType === ObjectTypes.ChatConversationMessage &&
            data.ClientGuid !== getClientGuid()
        );
    }

    private canStartOneToOneConversation(): boolean {
        return (
            this.conversation?.Type === FiChatConversationType.Group &&
            this.conversationMessage().IsUserAvailableForConversation
        );
    }

    private goToUserProfile(): void {
        this.sharedActionsService.openUserViewPopup(this.conversationMessage().CreatedByUserId);
    }

    private startOneToOneConversation(): void {
        this.fiChatService.triggerOpenOneToOneConversationByUserId(this.conversationMessage().CreatedByUserId);
    }

    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
